import React, { useEffect, useState } from 'react';
import { Download, Undo2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { getContactImportJobs, undoContactImport, downloadContactImportFile } from '../../../api/contacts';
import saveBlob from '../../../components/import/saveBlob';
import { formatMelbourneDateTime } from '../../../utils/melbourneTime';

/**
 * The last few Contacts imports, each with the file as uploaded and an undo
 * that moves the contacts it created to Trash (restorable from there). Until
 * 27 Oct 2026 a bad import could only be found and cleaned up by hand.
 */
const RecentContactImports = ({ onUndone }) => {
  const [jobs, setJobs] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => getContactImportJobs()
    .then((res) => setJobs((res.data.results || []).slice(0, 5)))
    .catch(() => setJobs([]));

  useEffect(() => { load(); }, []);

  const undo = async (job) => {
    // eslint-disable-next-line no-alert -- a deliberate, destructive step
    if (!window.confirm(`Undo "${job.filename}"? The ${job.created_count} contacts it created will move to Trash.`)) return;
    setBusy(job.id);
    try {
      const res = await undoContactImport(job.id);
      toast.success(`Moved ${res.data.archived} contacts to Trash. You can restore them from there.`);
      load();
      onUndone && onUndone();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not undo this import');
    } finally {
      setBusy(null);
    }
  };

  const download = async (job) => {
    try {
      saveBlob(await downloadContactImportFile(job.id), job.filename);
    } catch (e) {
      toast.error('Could not download the original file');
    }
  };

  if (!jobs || jobs.length === 0) return null;
  return (
    <div className="border-t border-gray-200 pt-4">
      <p className="text-sm font-medium text-gray-700 mb-2">Recent imports</p>
      <ul className="divide-y divide-gray-100 text-sm">
        {jobs.map((job) => (
          <li key={job.id} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-gray-900">{job.filename || 'Spreadsheet'}</p>
              <p className="text-xs text-gray-500">
                {formatMelbourneDateTime(job.created_at)} · {job.created_count} created, {job.updated_count} updated
                {job.status === 'undone' && ' · undone'}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {job.has_file && (
                <button onClick={() => download(job)} className="p-1.5 text-gray-500 hover:text-gray-800" title="Download the file as uploaded">
                  <Download className="w-4 h-4" />
                </button>
              )}
              {job.status !== 'undone' && job.created_count > 0 && (
                <button
                  onClick={() => undo(job)} disabled={busy === job.id}
                  className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Undo2 className="w-3.5 h-3.5" /> Undo
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RecentContactImports;
